// Shared helpers for the Blog and BlogEditor pages: error mapping, image
// upload, and the public URL of a post.

import { ConvexError } from 'convex/values';
import { activeConfig } from './environments';
import { friendlyError } from './errors';

export type PostStatus = 'draft' | 'scheduled' | 'published' | 'archived';

export const STATUS_LABEL: Record<PostStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  published: 'Published',
  archived: 'Archived',
};

export const STATUS_COLOR: Record<PostStatus, string> = {
  draft: 'var(--ink-3)',
  scheduled: '#B8860B',
  published: '#2E7D4F',
  archived: 'var(--ink-4)',
};

/** Mirrors LIMITS in src/convex/functions/blog/model.ts. */
export const BLOG_LIMITS = {
  titleMax: 140,
  seoTitleMax: 70,
  excerptMin: 50,
  excerptIdealMin: 150,
  excerptMax: 160,
  tagsMax: 10,
  tagNameMax: 40,
  coverAltMax: 300,
  authorMax: 80,
  ctaLabelMax: 60,
  pillarNameMax: 60,
  pillarDescriptionMax: 1000,
  seoDescriptionMax: 160,
} as const;

export const DEFAULT_AUTHOR = 'The WYDM Team';

export const CTA_OPTIONS = [
  { path: '/', label: 'Home' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/builder', label: 'Builder' },
  { path: '/agents', label: 'Agents' },
] as const;
export type CtaPath = (typeof CTA_OPTIONS)[number]['path'];

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/** The public site for the active environment (dev.wydm.app or wydm.app). */
export function publicPostUrl(slug: string): string {
  return `${activeConfig().webOrigin}/blog/${slug}`;
}

export function siteHost(): string {
  return new URL(activeConfig().webOrigin).host;
}

/** Which form field a backend error code belongs to, for inline display. */
export type BlogField =
  | 'title'
  | 'slug'
  | 'body'
  | 'excerpt'
  | 'pillar'
  | 'tags'
  | 'cover'
  | 'seoTitle'
  | 'canonicalUrl'
  | 'author'
  | 'cta'
  | 'schedule'
  | 'form';

const FIELD_FOR_CODE: Record<string, BlogField> = {
  INVALID_TITLE: 'title',
  DUPLICATE_TITLE: 'title',
  INVALID_SLUG: 'slug',
  SLUG_TAKEN: 'slug',
  EMPTY_BODY: 'body',
  BODY_TOO_LARGE: 'body',
  INVALID_EXCERPT: 'excerpt',
  PILLAR_NOT_FOUND: 'pillar',
  PILLAR_REQUIRED: 'pillar',
  INVALID_TAG: 'tags',
  TOO_MANY_TAGS: 'tags',
  INVALID_COVER: 'cover',
  UNSUPPORTED_TYPE: 'cover',
  INVALID_SEO_TITLE: 'seoTitle',
  INVALID_CANONICAL_URL: 'canonicalUrl',
  INVALID_AUTHOR: 'author',
  INVALID_CTA: 'cta',
  PUBLISH_AT_REQUIRED: 'schedule',
  PUBLISH_AT_IN_PAST: 'schedule',
  PUBLISH_AT_CONFLICT: 'schedule',
};

// Codes whose backend message is already written for people. Anything not
// listed here (or in FRIENDLY) falls back to friendlyError's generic text.
const PASS_THROUGH = new Set([
  ...Object.keys(FIELD_FOR_CODE),
  'PILLAR_IN_USE',
  'INVALID_NAME',
  'INVALID_DESCRIPTION',
  'INVALID_SEO_DESCRIPTION',
  'INVALID_ORDER',
  'NOT_FOUND',
  'TOO_MANY',
]);

const FRIENDLY: Record<string, string> = {
  FORBIDDEN: "Your admin account doesn't have blog access.",
  UNAUTHORIZED: 'Your session has expired. Sign in again.',
  INVALID_TOKEN: 'Your session has expired. Sign in again.',
  SESSION_REVOKED: 'Your session has expired. Sign in again.',
  PILLAR_REQUIRED: 'Choose a pillar.',
  PUBLISH_AT_REQUIRED: 'Pick a date and time.',
  PUBLISH_AT_IN_PAST: 'Pick a time in the future, or publish now.',
  EMPTY_BODY: 'Write the post before publishing it.',
  UNSUPPORTED_TYPE: 'Use a JPEG, PNG, WebP or GIF image.',
};

export interface BlogError {
  code: string;
  field: BlogField;
  message: string;
}

export function blogError(err: unknown, context: string): BlogError {
  if (err instanceof ConvexError) {
    const data = err.data as { code?: string; message?: string } | string;
    if (typeof data === 'object' && data && typeof data.code === 'string') {
      const field = FIELD_FOR_CODE[data.code] ?? 'form';
      const message =
        FRIENDLY[data.code] ??
        (PASS_THROUGH.has(data.code) && data.message ? data.message : friendlyError(err, context));
      return { code: data.code, field, message };
    }
  }
  if (err instanceof UploadError) return { code: 'UPLOAD_FAILED', field: 'form', message: err.message };
  return { code: 'UNKNOWN', field: 'form', message: friendlyError(err, context) };
}

export class UploadError extends Error {}

type Presign = (args: { token: string; contentType: string }) => Promise<{
  uploadUrl: string;
  key: string;
  url: string;
}>;

/** Presign, then PUT the file straight to R2. Returns the key and public URL. */
export async function uploadBlogImage(
  presign: Presign,
  token: string,
  file: File,
): Promise<{ key: string; url: string }> {
  if (!IMAGE_TYPES.includes(file.type)) {
    throw new UploadError(`${file.name}: use a JPEG, PNG, WebP or GIF image.`);
  }
  const { uploadUrl, key, url } = await presign({ token, contentType: file.type });
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) throw new UploadError(`Couldn't upload ${file.name} (HTTP ${res.status}).`);
  return { key, url };
}

/** "summer-date_ideas.jpg" → "Summer date ideas": a starting point for alt text. */
export function altFromFilename(name: string): string {
  const base = name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim();
  if (!base || /^(image|img|screenshot|photo|pasted)\b/i.test(base)) return '';
  return base.charAt(0).toUpperCase() + base.slice(1);
}

export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Epoch ms → the local "YYYY-MM-DDTHH:mm" a datetime-local input expects. */
export function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
